import { Component, DestroyRef, OnDestroy, inject } from '@angular/core';
import { SummaryOfChangesAsync } from '../store/summaryOfChanges/async';
import { ISummaryOfChangesState, ITransactionsRes } from '../store/summaryOfChanges/state';
import { forkJoin, Observable } from 'rxjs';
import { getSelectedAdminPlan } from '../login/store/selectors';
import { ILoginState } from '../login/store/state';
import moment from 'moment';
import { getTSearchResponse, getTSessionResponse, isFetchingUserTransactions } from '../store/summaryOfChanges/selector';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DateSearchPayload, SessionSearchPayload } from './summary-of-changes.component';

@Component({
    selector: 'app-summary-of-changes',
    template: `
      <app-summary-of-changes-page
        [selectedAdminPlan]="selectedAdminPlan()"
        [isFetching]="isFetchingUserTransactions()"
        [sessionRecords]="sessionRecords()"
        [searchTRecords]="searchRecords()"
        (getTBySession)="getTBySession($event)"
        (getTBySearch)="getTBySearch($event)"
        (getDetails)="getSelectedDetails($event)"
        (resetSearchClick)="resetTBySearch()"></app-summary-of-changes-page>
    `,
    standalone: false
})
export class SummaryOfChangesContainer implements OnDestroy {
  readonly luxId = 'BMT-summaryOfChanges' + this.constructor.name;
  private readonly destroyRef = inject(DestroyRef);

  readonly selectedAdminPlan = toSignal(this.lux.get(getSelectedAdminPlan, this.luxId), { initialValue: '' });
  readonly isFetchingUserTransactions = toSignal(this.lux.get(isFetchingUserTransactions, this.luxId), { initialValue: false });
  readonly sessionRecords = toSignal(this.lux.get(getTSessionResponse, this.luxId), { initialValue: { totalRecords: 0, transactions: [] } as ITransactionsRes });
  readonly searchRecords = toSignal(this.lux.get(getTSearchResponse, this.luxId), { initialValue: { totalRecords: 0, transactions: [] } as ITransactionsRes });

  constructor(
    private readonly scAsync: SummaryOfChangesAsync,
    private readonly lux: Lux<{ sChanges: ISummaryOfChangesState, login: ILoginState }>
  ) {
  }

  getTBySession(value: SessionSearchPayload): void {
    if (value?.event) {
      this.lux.set(state => state.sChanges.transactionsReq.pagination, { offset: value.event.pageSize * value.event.pageIndex, limit: value.event.pageSize });
    } else {
      this.lux.set(state => state.sChanges.transactionsReq.pagination, { offset: 0, limit: 25 });
    }
    this.runRequest(this.scAsync.getTBySessionId());
  }

  getTBySearch(value: DateSearchPayload): void {
    if (!value?.formValue) {
      return;
    }

    const req = {
      payers: value.payers,
      groupSpecifiedIdentifier: value.groupSpecifiedIdentifier,
      subgroupSpecifiedIdentifier: value.subgroupSpecifiedIdentifier,
      startDate: moment(value.formValue.startDate).format('YYYY-MM-DD'),
      endDate: moment(value.formValue.endDate).format('YYYY-MM-DD')
    };
    if (value?.event) {
      this.lux.set(state => state.sChanges.transactionsReq, { searchCriteria: req, pagination: { offset: value.event.pageSize * value.event.pageIndex, limit: value.event.pageSize } });
    } else {
      this.lux.set(state => state.sChanges.transactionsReq, { searchCriteria: req, pagination: { offset: 0, limit: 25 } });
    }
    this.runRequest(this.scAsync.getTBySearch());
  }

  resetTBySearch(): void {
    this.lux.set(state => state.sChanges.searchTransactionsRes, { totalRecords: 0, transactions: [] });
  }

  getSelectedDetails(transaction: ITransactionsRes['transactions'][number]): void {
    this.lux.set(state => state.sChanges.selectedTransaction, transaction);
    this.runRequest(forkJoin([
      this.scAsync.getMemberEligHistory(),
      this.scAsync.getCobHistory(),
      this.scAsync.getCoverageHistory(),
      this.scAsync.getPersonHistory(transaction.personId),
      this.scAsync.getAddressHistory(transaction.personId)
    ]));
  }

  private runRequest(request$: Observable<unknown>): void {
    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  ngOnDestroy(): void {
    this.resetTBySearch();
    this.lux.destroy(this.luxId);
  }
}
